import React from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Code2, Layout, Grid3x3, Box, Info } from 'lucide-react';

export function LayoutComponents() {
  return (
    <AppShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">
            Layout Components
          </h1>
          <p className="text-muted-foreground">
            Resizable panels and container layout components
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This page showcases shadcn/ui layout components.
          </AlertDescription>
        </Alert>

        {/* Flex Layout */}
        <Card className="p-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Flex Layout
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Flexible box layouts using Tailwind CSS
            </p>
          </div>

          {/* Horizontal Flex */}
          <div className="mb-8">
            <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Horizontal Flex
            </h4>
            <div className="h-64 border rounded-md overflow-hidden flex">
              <div className="flex-1 p-6 bg-muted/50">
                <div className="flex items-center gap-2 mb-3">
                  <Code2 className="w-5 h-5 text-blue-500" />
                  <h5 className="text-sm font-semibold text-foreground">
                    Left Panel
                  </h5>
                </div>
                <p className="text-sm text-muted-foreground">
                  This is the left panel using flex layout.
                </p>
                <div className="mt-4 space-x-2">
                  <Badge>Flexible</Badge>
                  <Badge variant="outline">Responsive</Badge>
                </div>
              </div>
              <div className="w-px bg-border" />
              <div className="flex-1 p-6 bg-background">
                <div className="flex items-center gap-2 mb-3">
                  <Box className="w-5 h-5 text-purple-500" />
                  <h5 className="text-sm font-semibold text-foreground">
                    Right Panel
                  </h5>
                </div>
                <p className="text-sm text-muted-foreground">
                  This is the right panel that adjusts automatically.
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Flex */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
              <Layout className="w-4 h-4 rotate-90" />
              Vertical Flex
            </h4>
            <div className="h-64 border rounded-md overflow-hidden flex flex-col">
              <div className="flex-1 p-6 bg-muted/50">
                <h5 className="text-sm font-semibold text-foreground mb-2">
                  Top Panel
                </h5>
                <p className="text-sm text-muted-foreground">
                  Vertical flex layouts are useful for stacked content.
                </p>
              </div>
              <div className="h-px bg-border" />
              <div className="flex-1 p-6 bg-background">
                <h5 className="text-sm font-semibold text-foreground mb-2">
                  Bottom Panel
                </h5>
                <p className="text-sm text-muted-foreground">
                  Panels stack vertically with equal or custom heights.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Grid Layout */}
        <Card className="p-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
              <Grid3x3 className="w-5 h-5" />
              Grid Layout
            </h3>
            <p className="text-sm text-muted-foreground">
              Responsive grid layout using Tailwind CSS
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }, (_, i) => (
              <Card
                key={i}
                className="p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={i % 4 === 0 ? 'default' : i % 4 === 1 ? 'secondary' : i % 4 === 2 ? 'destructive' : 'outline'}>
                    Item {i + 1}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Grid item with responsive columns that adapt to screen size.
                </p>
              </Card>
            ))}
          </div>
        </Card>

        {/* Container Max Width */}
        <Card className="p-0">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Container (Max-Width)
            </h3>
            <p className="text-sm text-muted-foreground">
              Content containers with different max-width breakpoints
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Small Container */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Small (max-w-sm / 640px)
              </h4>
              <div className="bg-muted/50 p-4 rounded-md">
                <div className="max-w-sm mx-auto">
                  <div className="p-4 bg-blue-500/10 rounded-md border border-blue-500/30">
                    <p className="text-sm text-foreground">
                      Small container content is centered with max-width of 640px
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Medium Container */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Medium (max-w-md / 768px)
              </h4>
              <div className="bg-muted/50 p-4 rounded-md">
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-green-500/10 rounded-md border border-green-500/30">
                    <p className="text-sm text-foreground">
                      Medium container content is centered with max-width of 768px
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Large Container */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Large (max-w-lg / 1024px)
              </h4>
              <div className="bg-muted/50 p-4 rounded-md">
                <div className="max-w-lg mx-auto">
                  <div className="p-4 bg-yellow-500/10 rounded-md border border-yellow-500/30">
                    <p className="text-sm text-foreground">
                      Large container content is centered with max-width of 1024px
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* XL Container */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Extra Large (max-w-xl / 1280px)
              </h4>
              <div className="bg-muted/50 p-4 rounded-md">
                <div className="max-w-xl mx-auto">
                  <div className="p-4 bg-purple-500/10 rounded-md border border-purple-500/30">
                    <p className="text-sm text-foreground">
                      Extra large container content is centered with max-width of 1280px
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Coming Soon */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            Coming Soon
          </h3>
          <p className="text-muted-foreground">
            The following components are being evaluated:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc list-inside">
            <li>Resizable Panels (shadcn/ui resizable component)</li>
            <li>Masonry Layout (evaluating masonry grid libraries)</li>
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}
